import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import Api from '@vacationtracker/shared/services/api'
import { ConversationSortKeyEnum } from '@vacationtracker/shared/types/conversation'
import { graphqlRequest, useGraphQLQuery } from '../../services/tanstack-query'
import { queryKeys } from '../../services/tanstack-query/queryClient'
import { getWebAgentConversationsPaginatedData, getWebAgentConversationMessagesData } from '../../graphql/custom-queries'
import { IVeeConversationInfo, IVeeMessageItem, IVeeSendResponse } from './types'

// Must outlive the worker Lambda (120s) so a slow-but-successful turn isn't reported as failed.
const REPLY_TIMEOUT_MS = 150_000

function pollInterval(waitedMs: number): number {
  if (waitedMs < 15_000) {
    return 2500
  }
  return waitedMs < 45_000 ? 5000 : 10_000
}

export function useDeleteVeeConversation() {
  const queryClient = useQueryClient()
  return useCallback(
    async (conversationId: string) => {
      await Api.post('/core/web-agent/delete', { conversationId })
      queryClient.removeQueries({ queryKey: queryKeys.veeAssistant.messages(conversationId) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.veeAssistant.conversations() })
    },
    [queryClient]
  )
}

interface IVeeConversationsPage {
  getWebAgentConversationsPaginated: {
    conversations: IVeeConversationInfo[] | null
    nextToken?: string | null
  } | null
}

export function useVeeConversations(enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.veeAssistant.conversations(),
    queryFn: ({ pageParam }) => graphqlRequest<IVeeConversationsPage>(getWebAgentConversationsPaginatedData, pageParam ? { nextToken: pageParam } : {}),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.getWebAgentConversationsPaginated?.nextToken || undefined,
    enabled,
    // The global 5min staleTime would hide new chats for minutes after reopening.
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const conversations = (query.data?.pages || []).flatMap((page) => page.getWebAgentConversationsPaginated?.conversations || [])

  return {
    conversations,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}

export type VeeSendOutcome = 'accepted' | 'limit' | 'failed'

export interface VeeSendOptions {
  confirmationToken?: string
  newConversation?: boolean
  kbSlug?: string
}

export interface VeeChatState {
  conversationId?: string
  messages: IVeeMessageItem[]
  waiting: boolean
  historyLoading: boolean
  pendingUserMessage: string | null
  timedOut: boolean
  sendFailed: boolean
  limitReached: number | null
  remaining: number | null
  send: (message: string, options?: VeeSendOptions) => Promise<VeeSendOutcome>
  retry: () => Promise<VeeSendOutcome>
  newChat: () => void
  openConversation: (id: string) => void
}

// POST returns 202 (the agent loop outruns API Gateway's 29s); poll until an assistant message newer than the baseline lands.
export function useVeeChat(): VeeChatState {
  const [conversationId, setConversationId] = useState<string | undefined>(undefined)
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null)
  const [waiting, setWaiting] = useState(false)
  const [timedOut, setTimedOut] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const [limitReached, setLimitReached] = useState<number | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  // Count AND server-side timestamp: the count catches replies with a stale-clock timestamp,
  // the timestamp catches a refetch that raced the baseline snapshot.
  const baselineRef = useRef<{ count: number; ts: string }>({ count: 0, ts: '' })
  const sentAtRef = useRef<number>(0)
  const lastSendRef = useRef<{ message: string; confirmationToken?: string } | null>(null)
  const queryClient = useQueryClient()

  const messagesQuery = useGraphQLQuery<{ getWebAgentConversationMessages: IVeeMessageItem[] }>(
    queryKeys.veeAssistant.messages(conversationId || ''),
    getWebAgentConversationMessagesData,
    { conversationId },
    {
      enabled: Boolean(conversationId),
      // The global 5min staleTime would show a stale transcript on reopen and poison the send baseline.
      staleTime: 0,
      refetchOnMount: 'always',
      gcTime: 60_000,
      refetchInterval: waiting ? () => pollInterval(Date.now() - sentAtRef.current) : false,
    }
  )

  const rawItems = messagesQuery.data?.getWebAgentConversationMessages
  const { messages, assistantCount, latestAssistantTimestamp } = useMemo(() => {
    const messageItems = (rawItems || []).filter((item) => item.type === ConversationSortKeyEnum.messages)
    const assistantItems = messageItems.filter((item) => item.role === 'assistant')
    return {
      messages: messageItems,
      assistantCount: assistantItems.length,
      latestAssistantTimestamp: assistantItems[assistantItems.length - 1]?.timestamp || '',
    }
  }, [rawItems])

  const replyArrived = assistantCount > baselineRef.current.count || (Boolean(latestAssistantTimestamp) && latestAssistantTimestamp > baselineRef.current.ts)

  useEffect(() => {
    if ((waiting || timedOut) && replyArrived) {
      setWaiting(false)
      setPendingUserMessage(null)
      setTimedOut(false)
      void queryClient.invalidateQueries({ queryKey: queryKeys.veeAssistant.conversations() })
    }
  }, [waiting, timedOut, replyArrived, queryClient])

  useEffect(() => {
    if (!waiting) {
      return
    }
    const timeout = setTimeout(() => {
      setWaiting(false)
      setTimedOut(true)
      // One last look - the reply may have landed between polls; the effect above clears the error if so.
      void queryClient.invalidateQueries({ queryKey: queryKeys.veeAssistant.messages(conversationId || '') })
    }, REPLY_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [waiting, conversationId, queryClient])

  const send = useCallback(
    async (message: string, options?: VeeSendOptions): Promise<VeeSendOutcome> => {
      const { confirmationToken, newConversation, kbSlug } = options || {}
      setTimedOut(false)
      setSendFailed(false)
      try {
        const response = await Api.post<{ message: string; conversationId?: string; confirmationToken?: string; kbSlug?: string }, IVeeSendResponse>('/core/web-agent', {
          message,
          conversationId: newConversation ? undefined : conversationId,
          confirmationToken,
          kbSlug,
        })
        if (response.limitReached) {
          setLimitReached(response.limit ?? 0)
          return 'limit'
        }
        if (response.accessDenied || !response.conversationId) {
          setSendFailed(true)
          return 'failed'
        }
        setLimitReached(null)
        setRemaining(response.remaining ?? null)
        baselineRef.current = newConversation ? { count: 0, ts: '' } : { count: assistantCount, ts: latestAssistantTimestamp }
        sentAtRef.current = Date.now()
        lastSendRef.current = { message, confirmationToken }
        setConversationId(response.conversationId)
        setPendingUserMessage(message)
        setWaiting(true)
        return 'accepted'
      } catch {
        setSendFailed(true)
        return 'failed'
      }
    },
    [conversationId, assistantCount, latestAssistantTimestamp]
  )

  const retry = useCallback(async (): Promise<VeeSendOutcome> => {
    if (!lastSendRef.current) {
      return 'failed'
    }
    return send(lastSendRef.current.message, { confirmationToken: lastSendRef.current.confirmationToken })
  }, [send])

  const reset = useCallback((id?: string) => {
    setConversationId(id)
    setPendingUserMessage(null)
    setWaiting(false)
    setTimedOut(false)
    setSendFailed(false)
    setLimitReached(null)
    baselineRef.current = { count: 0, ts: '' }
    lastSendRef.current = null
  }, [])

  const newChat = useCallback(() => reset(undefined), [reset])
  const openConversation = useCallback((id: string) => reset(id), [reset])

  return {
    conversationId,
    messages,
    waiting,
    historyLoading: Boolean(conversationId) && !messagesQuery.isFetched,
    pendingUserMessage,
    timedOut,
    sendFailed,
    limitReached,
    remaining,
    send,
    retry,
    newChat,
    openConversation,
  }
}
